import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Table, Tag, Tooltip } from "antd";
import "./threeM.css";
import { ColumnsType } from "antd/lib/table";
import {
  ThreeMActuals,
  ThreeMPhaseNode,
  TreeRow,
  aggWorkBasis,
  allExpandableKeys,
  buildTree,
  computeDeviation,
  fmtDevPct,
  fmtINRSigned,
  getActualRollup,
  getStatusColor,
  threeMRowClassName,
} from "./threeMShared";
import { useFontScale } from "../../../../theme/fontScale";
import {
  MUTED,
  boqBasisLine,
  compactAmountCell,
  perUnitLine,
} from "./threeMCells";
import { useWbsTreeName } from "../../shared/useWbsCodes";

// Planned vs Actual tab of the 3M Consolidation Report: the same
// Phase → Activity → Task tree, but every M carries a Planned | Actual |
// Deviation column trio (Total group first so the headline is visible without
// horizontal scrolling). Actuals come exclusively through getActualRollup —
// all zeros until the DPR / consumption / bills feed lands — so deviation
// cells render a muted "pending" placeholder instead of a wall of −100%.
//
// Every money cell carries a per-unit rate sub-line (₹ ÷ work quantity, in the
// row's work UOM) exactly as the 3M Breakdown tab does, so planned ₹/Cum can be
// read straight against actual ₹/Cum and the deviation's own ₹/Cum is the rate
// variance — the number site teams manage against. Parents show one only when
// their tasks share a UOM (aggWorkBasis); mixed-UOM parents show none.

// Group header text uses darker AA-contrast shades of the M card hues —
// the bright card colors (#1677ff / #fa8c16) fail contrast as text on white.
const GROUPS: Array<{
  title: string;
  color: string;
  field: keyof ThreeMActuals;
}> = [
  { title: "Total", color: "#1a1a2e", field: "totalAmount" },
  { title: "Material", color: "#0958d9", field: "materialAmount" },
  { title: "Manpower", color: "#d46b08", field: "manpowerAmount" },
  { title: "Machinery", color: "#722ed1", field: "machineryAmount" },
];

const PlannedVsActualTree: React.FC<{
  phases: ThreeMPhaseNode[];
  onViewTask: (taskId: number, taskName: string) => void;
}> = ({ phases, onViewTask }) => {
  // scale drives column widths too: at A++ (1.3×) hardcoded widths would wrap
  // currency values mid-number, so the whole grid grows proportionally.
  const { fs, scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);

  // scroll.x MUST be >= the sum of the rendered column widths or antd's fixed
  // (frozen) column misaligns against the scroll body. Each width is rounded
  // independently, so the sum is computed from the SAME rounded values — never
  // from w(total), which can come out a few px short at A+/A++.
  // Wider than the amounts alone need: the per-unit sub-lines carry full
  // precision plus a UOM suffix ("₹1,627.40/Cubic Meter") and must not wrap.
  const nameW = w(300);
  const trioW = (isTotal: boolean) =>
    isTotal ? [w(150), w(150), w(160)] : [w(140), w(140), w(150)];
  const scrollX =
    nameW +
    GROUPS.reduce(
      (sum, group) =>
        sum +
        trioW(group.field === "totalAmount").reduce((a, b) => a + b, 0),
      0
    );

  const treeData = useMemo(() => buildTree(phases), [phases]);
  // Phase/activity names embed a WBS code; task names are already clean.
  const treeLabel = useWbsTreeName(treeData);
  const expandKeys = useMemo(() => allExpandableKeys(treeData), [treeData]);

  // Controlled expansion so "expand all phases" survives async data loads.
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(
    []
  );
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [treeData]);

  // Planned project totals = sum of the top-level phase rollups, so the pinned
  // row always matches the filtered view.
  const plannedTotals = useMemo(
    () =>
      treeData.reduce<ThreeMActuals>(
        (acc, row) => ({
          materialAmount: acc.materialAmount + row.rollup.materialAmount,
          manpowerAmount: acc.manpowerAmount + row.rollup.manpowerAmount,
          machineryAmount: acc.machineryAmount + row.rollup.machineryAmount,
          totalAmount: acc.totalAmount + row.rollup.totalAmount,
        }),
        {
          materialAmount: 0,
          manpowerAmount: 0,
          machineryAmount: 0,
          totalAmount: 0,
        }
      ),
    [treeData]
  );
  const actualTotals = getActualRollup({ key: "PRJ", id: 0 });

  // Project-level work basis for the pinned row's per-unit rates: Σ of every
  // task's work quantity, and only when the whole project shares one UOM —
  // a project mixing m³ with KWh has no single rate to quote (aggWorkBasis
  // returns {} and the sub-lines simply don't render).
  const projectBasis = useMemo(
    () =>
      aggWorkBasis(
        (phases || []).flatMap((phase) =>
          (phase.activities || []).flatMap((activity) => activity.tasks || [])
        )
      ),
    [phases]
  );

  // actual === 0 ⇒ this M has no actuals fed yet: show a neutral "pending"
  // placeholder, NOT the computed −₹planned / −100% (which would scream
  // "everything is 100% behind" while the feed simply isn't live). The grey
  // also doubles as a partial-feed indicator once some Ms have actuals.
  const deviationCell = (
    basis: { workQuantity?: string; workUom?: string },
    planned: number,
    actual: number
  ) => {
    if (actual === 0) {
      return (
        <div style={{ color: MUTED, whiteSpace: "nowrap" }}>
          <span>—</span>
          <div style={{ fontSize: fs(11) }}>pending</div>
        </div>
      );
    }
    const dev = computeDeviation(planned, actual);
    const color = dev.amount > 0 ? "#cf1322" : "#237804";
    return (
      <div>
        <Tooltip title={fmtINRSigned(dev.amount, true)}>
          <span
            style={{
              color,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {fmtINRSigned(dev.amount)}
          </span>
        </Tooltip>
        {planned > 0 ? (
          <div
            style={{
              fontSize: fs(11),
              color,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {fmtDevPct(dev.pct)}
          </div>
        ) : null}
        {/* Rate variance: ₹ per unit of work above (+) or below (−) plan. */}
        {perUnitLine(basis, dev.amount, fs, true)}
      </div>
    );
  };

  const nameColumn: ColumnsType<TreeRow>[number] = {
    title: "Phase / Activity / Task",
    dataIndex: "name",
    key: "name",
    width: nameW,
    fixed: "left",
    render: (text: string, record: TreeRow) => {
      const key = record.key || "";
      const statusTag = record.status ? (
        <Tag
          color={getStatusColor(record.status)}
          style={{ fontSize: fs(10.5), lineHeight: 1.5, marginLeft: 6 }}
        >
          {record.status}
        </Tag>
      ) : null;

      // Block-level flex column: sits BESIDE the floated indent/expand icon
      // (threeM.css) taking the remaining width, so wrapped lines stay
      // aligned under the cell's own left edge instead of dropping below the
      // tree indent. Must stay block-level (not inline-flex) — atomic inline
      // boxes drop to the next line when the icon leaves too little room.
      const cellBox: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        textAlign: "left",
        minWidth: 0,
      };

      if (key.startsWith("P-")) {
        return (
          <div style={cellBox}>
            <span
              style={{ fontWeight: 700, fontSize: fs(13.5), color: "#1f1f1f" }}
            >
              {treeLabel(key, text)}
              {statusTag}
            </span>
            {boqBasisLine(record, fs, { prefix: "Σ " })}
          </div>
        );
      }
      if (key.startsWith("A-")) {
        return (
          <div style={cellBox}>
            <span
              style={{ fontWeight: 600, fontSize: fs(12.5), color: "#262626" }}
            >
              {treeLabel(key, text)}
              {statusTag}
            </span>
            {boqBasisLine(record, fs, { prefix: "Σ " })}
          </div>
        );
      }
      return (
        <div style={cellBox}>
          {record.sequenceId ? (
            <span style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.sequenceId}
            </span>
          ) : null}
          <span style={{ fontSize: fs(12.5), color: "#262626" }}>
            <span
              className="inlineTableCellLink"
              onClick={() => onViewTask(record.id, record.name)}
            >
              {text}
            </span>
            {statusTag}
          </span>
          {/* Quantity @ BOQ rate = value, under the task name. */}
          {boqBasisLine(record, fs)}
        </div>
      );
    },
  };

  const columns: ColumnsType<TreeRow> = [
    nameColumn,
    ...GROUPS.map((group) => {
      // Widths come from the SAME trioW the scroll width was summed from.
      const [plannedW, actualW, devW] = trioW(group.field === "totalAmount");
      return {
        title: (
          <span style={{ color: group.color, fontWeight: 600 }}>
            {group.title}
          </span>
        ),
        key: group.field,
        children: [
          {
            title: "Planned ₹",
            key: `${group.field}-planned`,
            width: plannedW,
            align: "right" as const,
            render: (_: any, record: TreeRow) => (
              <div>
                {compactAmountCell(record.rollup[group.field])}
                {perUnitLine(record, record.rollup[group.field], fs)}
              </div>
            ),
          },
          {
            title: "Actual ₹",
            key: `${group.field}-actual`,
            width: actualW,
            align: "right" as const,
            render: (_: any, record: TreeRow) => (
              <div>
                {compactAmountCell(getActualRollup(record)[group.field])}
                {perUnitLine(record, getActualRollup(record)[group.field], fs)}
              </div>
            ),
          },
          {
            title: "Deviation (₹ · %)",
            key: `${group.field}-deviation`,
            width: devW,
            align: "right" as const,
            render: (_: any, record: TreeRow) =>
              deviationCell(
                record,
                record.rollup[group.field],
                getActualRollup(record)[group.field]
              ),
          },
        ],
      };
    }),
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
        message="Actuals not captured yet"
        description="Actual spend will populate from the DPR / consumption / bills feed once it's live. Until then Actual shows ₹0 and Deviation stays blank; each cell turns red (over) or green (under) as its actuals land. The grey sub-line under every amount is its per-unit rate (₹ ÷ work quantity) — on the Deviation column that reads as the rate variance."
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ marginLeft: "auto" }}>
          <Button
            type="link"
            size="small"
            disabled={expandKeys.length === 0}
            onClick={() => setExpandedRowKeys(expandKeys)}
          >
            Expand all
          </Button>
          <Button
            type="link"
            size="small"
            disabled={expandedRowKeys.length === 0}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
        </div>
      </div>
      <Table
        className="customTable taskHierarchyTable threeMTree"
        columns={columns}
        dataSource={treeData}
        pagination={false}
        bordered
        size="middle"
        // Keep the (grouped) header pinned to the viewport top while
        // scrolling down the tree; the app nav is not position-fixed, so no
        // offset is needed. Also pins the horizontal scrollbar to the
        // viewport bottom for this wide table.
        sticky={{ offsetHeader: 0 }}
        scroll={{ x: scrollX }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
        }}
        rowClassName={threeMRowClassName}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: "#fafafa" }}>
              <Table.Summary.Cell index={0}>
                <span style={{ fontWeight: 700, fontSize: fs(12.5) }}>
                  Project Total
                </span>
              </Table.Summary.Cell>
              {GROUPS.flatMap((group, gi) => [
                <Table.Summary.Cell
                  key={`${group.field}-planned`}
                  index={gi * 3 + 1}
                  align="right"
                >
                  {compactAmountCell(plannedTotals[group.field], true)}
                  {perUnitLine(projectBasis, plannedTotals[group.field], fs)}
                </Table.Summary.Cell>,
                <Table.Summary.Cell
                  key={`${group.field}-actual`}
                  index={gi * 3 + 2}
                  align="right"
                >
                  {compactAmountCell(actualTotals[group.field])}
                  {perUnitLine(projectBasis, actualTotals[group.field], fs)}
                </Table.Summary.Cell>,
                <Table.Summary.Cell
                  key={`${group.field}-deviation`}
                  index={gi * 3 + 3}
                  align="right"
                >
                  {deviationCell(
                    projectBasis,
                    plannedTotals[group.field],
                    actualTotals[group.field]
                  )}
                </Table.Summary.Cell>,
              ])}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </>
  );
};

export default PlannedVsActualTree;
